import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  Image,
  Upload,
  Tags,
  LogOut,
  Home,
  Menu,
  X,
  Images,
  SlidersHorizontal,
  BookOpen,
  Inbox,
  Music,
  Building2,
  Share2,
  ShoppingBag,
  Trophy,
  Users,
  Calendar,
  Palette,
  Mail,
  Megaphone,
  Rocket,
  Camera,
  Monitor,
  Mountain,
  Radio,
  CalendarDays,
  AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/App";
import { useState } from "react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_pressnet/artifacts/nmfi6tck_Choluteca_net3-BLANCO.png";

const navItems = [
  { name: "Dashboard", href: "/admin", icon: LayoutDashboard },
  { name: "Posts", href: "/admin/posts", icon: FileText },
  { name: "Reportes", href: "/admin/reportes", icon: AlertCircle },
  { name: "Eventos", href: "/admin/eventos", icon: CalendarDays },
  { name: "Memorias", href: "/admin/memorias", icon: Image },
  { name: "Submissions", href: "/admin/submissions", icon: Inbox },
  { name: "Guías", href: "/admin/guias", icon: BookOpen },
  { name: "Galerías", href: "/admin/galerias", icon: Images },
  { name: "Antes y Ahora", href: "/admin/antes-ahora", icon: SlidersHorizontal },
  { name: "Artistas", href: "/admin/artistas", icon: Music },
  { name: "Directorio", href: "/admin/directorio", icon: Building2 },
  { name: "Destinos", href: "/admin/destinos", icon: Mountain },
  { name: "En Vivo", href: "/admin/en-vivo", icon: Radio },
  { name: "Marketplace", href: "/admin/marketplace", icon: ShoppingBag },
  { name: "Tienda", href: "/admin/tienda", icon: ShoppingBag },
  { name: "--- Solicitudes ---", href: "#", icon: Inbox, isHeader: true },
  { name: "Contacto", href: "/admin/contacto", icon: Mail },
  { name: "Emprendedores", href: "/admin/emprendedores", icon: Rocket },
  { name: "Publicidad", href: "/admin/publicidad", icon: Megaphone },
  { name: "Contenido Comunitario", href: "/admin/contenido-comunitario", icon: Camera },
  { name: "--- Deportes ---", href: "#", icon: Trophy, isHeader: true },
  { name: "Transmisiones", href: "/admin/transmisiones", icon: Monitor },
  { name: "Equipos", href: "/admin/equipos", icon: Users },
  { name: "Torneos", href: "/admin/torneos", icon: Trophy },
  { name: "Partidos", href: "/admin/partidos", icon: Calendar },
  { name: "--- Config ---", href: "#", icon: Tags, isHeader: true },
  { name: "Usuarios", href: "/admin/usuarios", icon: Users },
  { name: "Newsletter", href: "/admin/newsletter", icon: Mail },
  { name: "Portadas", href: "/admin/portadas", icon: Palette },
  { name: "Redes Sociales", href: "/admin/redes-sociales", icon: Share2 },
  { name: "Media", href: "/admin/media", icon: Image },
  { name: "Importar XML", href: "/admin/import", icon: Upload },
  { name: "Categorías", href: "/admin/categories", icon: Tags },
];

export default function AdminLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const isActive = (href) => {
    if (href === "/admin") return location.pathname === "/admin";
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-brand-bg flex">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 w-64 bg-white border-r border-gray-100 z-50 transform transition-transform lg:transform-none ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="admin-sidebar"
      >
        <div className="flex flex-col h-full overflow-hidden">
          {/* Logo */}
          <div className="p-6 border-b border-gray-100">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Choluteca.net" className="h-10" />
            </Link>
          </div>

          {/* Nav Links - Scrollable container */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              item.isHeader ? (
                <div key={item.name} className="px-3 py-2 mt-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  {item.name.replace(/---/g, "").trim()}
                </div>
              ) : (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`admin-sidebar-link ${isActive(item.href) ? "active" : ""}`}
                  data-testid={`admin-nav-${item.name.toLowerCase().replace(" ", "-")}`}
                >
                  <item.icon className="h-5 w-5 mr-3" />
                  {item.name}
                </Link>
              )
            ))}
          </nav>

          {/* User & Logout */}
          <div className="p-4 border-t border-gray-100">
            <div className="flex items-center gap-3 px-3 py-2 mb-2">
              {user?.picture ? (
                <img
                  src={user.picture}
                  alt={user.name}
                  className="w-8 h-8 rounded-full"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-medium">
                  {user?.name?.charAt(0) || "A"}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-brand-text truncate">
                  {user?.name}
                </p>
                <p className="text-xs text-brand-textMuted truncate">
                  {user?.email}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Link to="/" className="flex-1">
                <Button variant="outline" size="sm" className="w-full">
                  <Home className="h-4 w-4 mr-2" />
                  Sitio
                </Button>
              </Link>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                data-testid="admin-logout-btn"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Mobile Header */}
        <header className="lg:hidden bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 hover:bg-gray-100 rounded-lg"
            data-testid="mobile-menu-toggle"
          >
            <Menu className="h-6 w-6" />
          </button>
          <img src={LOGO_URL} alt="Choluteca.net" className="h-8" />
          <div className="w-10" />
        </header>

        {/* Content */}
        <main className="flex-1 p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
